import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

const LANGUAGE_STORAGE_KEY = "mypetplus-admin-language";
const DEFAULT_LANGUAGE = "it";

const translations = {
  en: {
    common: {
      appName: "MyPetPlus Admin",
      language: "Language",
      italian: "Italiano",
      english: "English",
      switchTo: "Switch to {language}",
      searchHere: "Search here",
      loading: "Loading...",
      noDetails: "No details available",
      noData: "No data available",
      noResults: "No results found",
      all: "All",
      allRoles: "All Roles",
      allStatus: "All Status",
      status: "Status",
      action: "Action",
      actions: "Actions",
      view: "View",
      viewDetails: "View Details",
      edit: "Edit",
      save: "Save",
      create: "Create",
      cancel: "Cancel",
      close: "Close",
      delete: "Delete",
      approve: "Approve",
      reject: "Reject",
      yes: "Yes",
      no: "No",
      active: "Active",
      enabled: "Enabled",
      disabled: "Disabled",
      required: "This field is required",
      nameRequired: "Name is required",
      priceRequired: "Price is required",
      ownerIdRequired: "Owner ID is required",
      selectSpecies: "Select at least one species",
      reason: "Reason",
      unknown: "Unknown",
      days: "Days",
      day: "day",
      daysLower: "days",
      created: "Created",
      email: "Email",
      phone: "Phone",
      name: "Name",
      id: "ID",
      type: "Type",
      description: "Description",
      category: "Category",
      date: "Date",
      time: "Time",
      payment: "Payment",
      amount: "Amount",
      total: "Total",
      user: "User",
      provider: "Provider",
      pharmacy: "Pharmacy",
      veterinarian: "Veterinarian",
      petOwner: "Pet Owner",
      pet: "Pet",
      product: "Product",
    },
    nav: {
      main: "Main",
      dashboard: "Dashboard",
      approvals: "Approvals",
      veterinarians: "Veterinarians",
      petStores: "Pet Stores",
      users: "All Users",
      petsCare: "Pets & Care",
      platformServices: "Platform Services",
      pets: "Pets",
      medicalRecords: "Medical Records",
      vaccines: "Vaccines",
      appointments: "Appointments",
      communication: "Communication",
      doctorMessages: "Doctor Messages",
      pharmacyMessages: "Pharmacy Texts",
      supportTickets: "Support Tickets",
      contactQueries: "Contact Us Queries",
      commerce: "Commerce",
      products: "Products",
      orders: "Orders",
      finance: "Finance",
      transactions: "Transactions",
      withdrawalRequests: "Withdrawal Requests",
      subscriptions: "Subscriptions",
      subscriptionPlans: "Subscription Plans",
      content: "Content",
      announcements: "Announcements",
      reviews: "Reviews",
      configuration: "Configuration",
      insuranceCompanies: "Insurance Companies",
      specializations: "Specializations",
      footerOptions: "Footer Options",
      changePassword: "Change Password",
      logout: "Logout",
    },
    sidebar: {
      item: "item",
      items: "items",
      needReview: "need review",
      newData: "New data available",
    },
    login: {
      title: "Admin Login",
      email: "Email",
      password: "Password",
      required: "Email and password are required",
      invalidResponse: "Invalid login response",
      adminOnly: "Only admin accounts can access this panel",
      failed: "Login failed",
      loggingIn: "Logging in...",
      submit: "Login",
    },
    dashboard: {
      dashboard: "Dashboard",
      title: "Admin Dashboard",
      recentVeterinarians: "Recent Veterinarians",
      showAll: "Show all",
      noVeterinarians: "No veterinarians",
      goodMorning: "Good Morning,",
      admin: "Admin",
      niceDay: "Have a nice day at work",
      deliveryPerformance: "Pharmacy Delivery Performance",
      deliveryHint: "Late orders update automatically after their promised delivery date.",
      viewOrders: "View Orders",
      pharmacy: "Pharmacy",
      totalOrders: "Total Orders",
      onTime: "On Time",
      late: "Late",
      awaitingDelivery: "Awaiting Delivery",
      averageDeliveryTime: "Average Delivery Time",
      onTimeDelivery: "On-Time Delivery",
      noDeliveryData: "No pharmacy delivery data yet.",
      appointments: "Appointments",
      petOwners: "Pet Owners",
      veterinarians: "Veterinarians",
      earnings: "Earnings",
      vsLastMonth: "vs last month",
      upcomingAppointments: "Upcoming Appointments",
      noAppointments: "No appointments",
      number: "No",
      myPetName: "My Pet name",
      doctor: "Doctor",
      disease: "Disease",
      recentPets: "Recent Pets",
      age: "Age",
      dateOfBirth: "Date of Birth",
      diagnosis: "Diagnosis",
      triage: "Triage",
      noPets: "No pets",
      active: "Active",
      deleteConfirm: "Are you sure you want to delete this?",
      close: "Close",
      delete: "Delete",
      loadFailed: "Failed to load dashboard",
    },
    status: {
      approved: "Approved",
      pending: "Pending",
      rejected: "Rejected",
      blocked: "Blocked",
      active: "Active",
      inactive: "Inactive",
      completed: "Completed",
      cancelled: "Cancelled",
      expired: "Expired",
      awaitingDelivery: "Awaiting Delivery",
      confirmed: "Confirmed",
      processing: "Processing",
      shipped: "Shipped",
      delivered: "Delivered",
      refunded: "Refunded",
      success: "Success",
      failed: "Failed",
      none: "None",
      yes: "Yes",
      no: "No",
    },
    admin: {
      labels: {
        admin: "Admin",
        approvals: "Approvals",
        users: "Users",
        "pets-care": "Pets & Care",
        commerce: "Commerce",
        finance: "Finance",
        configuration: "Configuration",
        "veterinarian-approvals": "Veterinarian Approvals",
        "pet-store-approvals": "Pet Store Approvals",
        "all-roles": "All Roles",
        "all-status": "All Status",
        "all-payments": "All Payments",
        "all-types": "All Types",
        "all-sellers": "All Sellers",
        "all-pets": "All Pets",
        "all-subscriptions": "All Subscriptions",
        "active-only": "Active Only",
        "include-inactive": "Include Inactive",
        "duration-days": "Duration (days)",
        "pet-store-id": "PetStore ID",
        "pet-owner-id": "PetOwner ID",
        "my-pet-name": "My Pet name",
        "date-of-birth": "Date of Birth",
        "provider-roles": "Provider roles",
        "total-orders": "Total Orders",
        "on-time": "On Time",
        "awaiting-delivery": "Awaiting Delivery",
        "average-delivery-time": "Average Delivery Time",
        "on-time-delivery": "On-Time Delivery",
        "view-orders": "View Orders",
        "show-all": "Show all",
        "search-here": "Search here",
        "no-results": "No results found",
        "no-details": "No details available",
        "view-details": "View Details",
        "edit-price": "Edit Price",
        "retry-payout": "Retry payout",
        "add-vaccine": "Add Vaccine",
        "add-pet-store": "Add Pet Store",
        "add-product": "Add Product",
        "add-specialization": "Add Specialization",
        "add-insurance": "Add Insurance",
        "add-announcement": "Add Announcement",
        "edit-product": "Edit Product",
        "edit-pet-store": "Edit Pet Store",
        "edit-vaccine": "Edit Vaccine",
        "edit-plan-price": "Edit Plan Price",
        "edit-plan": "Edit Plan",
        "edit-specialization": "Edit Specialization",
        "edit-insurance-company": "Edit Insurance Company",
        "edit-announcement": "Edit Announcement",
        "add-plan": "Add Plan",
        "order-details": "Order Details",
        "transaction-details": "Transaction Details",
        "approve-withdrawal": "Approve Withdrawal",
        "reject-withdrawal": "Reject Withdrawal",
        "appointment-details": "Appointment Details",
        "veterinarian-application-review": "Veterinarian application review",
        "delivery-commitment-monitoring": "Delivery Commitment & Monitoring",
        "no-active-subscription": "No active subscription.",
        "purchased-subscription": "Purchased subscription",
        "registration-documents": "Registration documents",
        "no-registration-documents": "No registration documents have been uploaded.",
        "open": "Open",
        "loading": "Loading...",
        "no-data": "No details",
        "save-changes": "Save Changes",
        "create-service": "Create Service",
        "edit-service": "Edit Service",
        "add-service": "Add Service",
        "platform-services": "Platform Services",
        "footer-options": "Footer Options",
        "contact-queries": "Contact Us Queries",
        "support-tickets": "Support Tickets",
        "doctor-messages": "Doctor Messages",
        "pharmacy-parapharmacy-messages": "Pharmacy Texts",
        "insurance-companies": "Insurance Companies",
        "specializations": "Specializations",
        "medical-records": "Medical Records",
        "withdrawal-requests": "Withdrawal Requests",
        "subscription-plans": "Subscription Plans",
        "pet-stores": "Pet Stores",
        "pet-owner": "Pet Owner",
        veterinarian: "Veterinarian",
        pharmacy: "Pharmacy",
        parapharmacy: "Parapharmacy",
        "pet-sitter": "Pet Sitter",
        "name-required": "Name is required",
        "price-required": "Price is required",
        "owner-id-required": "Owner ID is required",
        "select-at-least-one-species": "Select at least one species",
        "delivery-status": "Delivery status",
        "request-id": "Request ID",
        "stripe-connected-account": "Stripe Connected Account",
        "withdrawal-fee-percent-optional": "Withdrawal fee percent (optional)",
        fee: "Fee",
        "net-payout": "Net payout",
        reason: "Reason",
        number: "No",
        qty: "Qty",
        sku: "SKU",
        "discount-price": "Discount Price",
        stock: "Stock",
        "sub-category": "Sub Category",
        "pet-types": "Pet Types",
        "tags-comma-separated": "Tags (comma separated)",
        "requires-prescription": "Requires Prescription",
        images: "Images",
        logo: "Logo",
        "owner-id": "Owner ID",
        "address-line-1": "Address Line 1",
        city: "City",
        state: "State",
        country: "Country",
        zip: "Zip",
        "applicable-species": "Applicable Species",
        "min-age-weeks": "Min Age (weeks)",
        "doses-required": "Doses Required",
        plan: "Plan",
        title: "Title",
        message: "Message",
        link: "Link",
        file: "File",
        priority: "Priority",
        "announcement-type": "Announcement Type",
        pinned: "Pinned",
        "expiry-type": "Expiry Type",
        "expiry-date": "Expiry Date",
        "target-specializations": "Target Specializations",
        "target-subscription-plans": "Target Subscription Plans",
        "target-location-city": "Target Location City",
        "target-location-state": "Target Location State",
        "target-location-country": "Target Location Country",
        "individual-veterinarian-ids-comma-separated": "Individual Veterinarian IDs (comma separated)",
      },
    },
  },
  it: {
    common: {
      appName: "Amministrazione MyPetPlus",
      language: "Lingua",
      italian: "Italiano",
      english: "English",
      switchTo: "Passa a {language}",
      searchHere: "Cerca qui",
      loading: "Caricamento...",
      noDetails: "Nessun dettaglio disponibile",
      noData: "Nessun dato disponibile",
      noResults: "Nessun risultato trovato",
      all: "Tutti",
      allRoles: "Tutti i ruoli",
      allStatus: "Tutti gli stati",
      status: "Stato",
      action: "Azione",
      actions: "Azioni",
      view: "Visualizza",
      viewDetails: "Visualizza dettagli",
      edit: "Modifica",
      save: "Salva",
      create: "Crea",
      cancel: "Annulla",
      close: "Chiudi",
      delete: "Elimina",
      approve: "Approva",
      reject: "Rifiuta",
      yes: "Sì",
      no: "No",
      active: "Attivo",
      enabled: "Abilitato",
      disabled: "Disabilitato",
      required: "Questo campo è obbligatorio",
      nameRequired: "Il nome è obbligatorio",
      priceRequired: "Il prezzo è obbligatorio",
      ownerIdRequired: "L'ID del proprietario è obbligatorio",
      selectSpecies: "Seleziona almeno una specie",
      reason: "Motivo",
      unknown: "Sconosciuto",
      days: "Giorni",
      day: "giorno",
      daysLower: "giorni",
      created: "Creato",
      email: "Email",
      phone: "Telefono",
      name: "Nome",
      id: "ID",
      type: "Tipo",
      description: "Descrizione",
      category: "Categoria",
      date: "Data",
      time: "Ora",
      payment: "Pagamento",
      amount: "Importo",
      total: "Totale",
      user: "Utente",
      provider: "Fornitore",
      pharmacy: "Farmacia",
      veterinarian: "Veterinario",
      petOwner: "Proprietario",
      pet: "Animale",
      product: "Prodotto",
    },
    nav: {
      main: "Principale",
      dashboard: "Dashboard",
      approvals: "Approvazioni",
      veterinarians: "Veterinari",
      petStores: "Negozi per animali",
      users: "Tutti gli utenti",
      petsCare: "Pets e cura",
      platformServices: "Servizi della piattaforma",
      pets: "Pets",
      medicalRecords: "Cartelle cliniche",
      vaccines: "Vaccini",
      appointments: "Appuntamenti",
      communication: "Comunicazione",
      doctorMessages: "Messaggi ai veterinari",
      pharmacyMessages: "Testi farmacia",
      supportTickets: "Ticket di assistenza",
      contactQueries: "Richieste di contatto",
      commerce: "Commercio",
      products: "Prodotti",
      orders: "Ordini",
      finance: "Finanza",
      transactions: "Transazioni",
      withdrawalRequests: "Richieste di prelievo",
      subscriptions: "Abbonamenti",
      subscriptionPlans: "Piani di abbonamento",
      content: "Contenuti",
      announcements: "Annunci",
      reviews: "Recensioni",
      configuration: "Configurazione",
      insuranceCompanies: "Compagnie assicurative",
      specializations: "Specializzazioni",
      footerOptions: "Opzioni footer",
      changePassword: "Cambia password",
      logout: "Esci",
    },
    sidebar: {
      item: "elemento",
      items: "elementi",
      needReview: "da esaminare",
      newData: "Nuovi dati disponibili",
    },
    login: {
      title: "Accesso amministratore",
      email: "Email",
      password: "Password",
      required: "Email e password sono obbligatorie",
      invalidResponse: "Risposta di accesso non valida",
      adminOnly: "Solo gli account amministratore possono accedere a questo pannello",
      failed: "Accesso non riuscito",
      loggingIn: "Accesso in corso...",
      submit: "Accedi",
    },
    dashboard: {
      dashboard: "Dashboard",
      title: "Dashboard amministratore",
      recentVeterinarians: "Veterinari recenti",
      showAll: "Mostra tutti",
      noVeterinarians: "Nessun veterinario",
      goodMorning: "Buongiorno,",
      admin: "Amministratore",
      niceDay: "Buona giornata di lavoro",
      deliveryPerformance: "Andamento consegne farmacie",
      deliveryHint: "Gli ordini in ritardo si aggiornano automaticamente dopo la data di consegna promessa.",
      viewOrders: "Visualizza ordini",
      pharmacy: "Farmacia",
      totalOrders: "Ordini totali",
      onTime: "Puntuali",
      late: "In ritardo",
      awaitingDelivery: "In attesa di consegna",
      averageDeliveryTime: "Tempo medio di consegna",
      onTimeDelivery: "Consegne puntuali",
      noDeliveryData: "Nessun dato sulle consegne delle farmacie.",
      appointments: "Appuntamenti",
      petOwners: "Proprietari",
      veterinarians: "Veterinari",
      earnings: "Guadagni",
      vsLastMonth: "rispetto al mese scorso",
      upcomingAppointments: "Prossimi appuntamenti",
      noAppointments: "Nessun appuntamento",
      number: "N.",
      myPetName: "Nome animale",
      doctor: "Veterinario",
      disease: "Patologia",
      recentPets: "Pets recenti",
      age: "Età",
      dateOfBirth: "Data di nascita",
      diagnosis: "Diagnosi",
      triage: "Triage",
      noPets: "Nessun Pets",
      active: "Attivo",
      deleteConfirm: "Sei sicuro di voler eliminare questo elemento?",
      close: "Chiudi",
      delete: "Elimina",
      loadFailed: "Impossibile caricare la dashboard",
    },
    status: {
      approved: "Approvato",
      pending: "In attesa",
      rejected: "Rifiutato",
      blocked: "Bloccato",
      active: "Attivo",
      inactive: "Inattivo",
      completed: "Completato",
      cancelled: "Annullato",
      expired: "Scaduto",
      awaitingDelivery: "In attesa di consegna",
      confirmed: "Confermato",
      processing: "In elaborazione",
      shipped: "Spedito",
      delivered: "Consegnato",
      refunded: "Rimborsato",
      success: "Riuscito",
      failed: "Fallito",
      none: "Nessuno",
      yes: "Sì",
      no: "No",
    },
    admin: {
      labels: {
        admin: "Amministratore",
        approvals: "Approvazioni",
        users: "Utenti",
        "pets-care": "Pets e cura",
        commerce: "Commercio",
        finance: "Finanza",
        configuration: "Configurazione",
        "veterinarian-approvals": "Approvazioni veterinari",
        "pet-store-approvals": "Approvazioni negozi per animali",
        veterinarians: "Veterinari",
        pets: "Pets",
        appointments: "Appuntamenti",
        products: "Prodotti",
        orders: "Ordini",
        transactions: "Transazioni",
        payments: "Pagamenti",
        subscriptions: "Abbonamenti",
        announcements: "Annunci",
        reviews: "Recensioni",
        uploads: "Caricamenti",
        dashboard: "Dashboard",
        "all-users": "Tutti gli utenti",
        "all-roles": "Tutti i ruoli",
        "all-status": "Tutti gli stati",
        "all-payments": "Tutti i pagamenti",
        "all-types": "Tutti i tipi",
        "all-sellers": "Tutti i venditori",
        "all-pets": "Tutti i Pets",
        "all-subscriptions": "Tutti gli abbonamenti",
        "active-only": "Solo attivi",
        "include-inactive": "Includi inattivi",
        "duration-days": "Durata (giorni)",
        "pet-store-id": "ID negozio",
        "pet-owner-id": "ID proprietario",
        "my-pet-name": "Nome animale",
        "date-of-birth": "Data di nascita",
        "provider-roles": "Ruoli fornitori",
        "total-orders": "Ordini totali",
        "on-time": "Puntuali",
        "awaiting-delivery": "In attesa di consegna",
        "average-delivery-time": "Tempo medio di consegna",
        "on-time-delivery": "Consegne puntuali",
        "view-orders": "Visualizza ordini",
        "show-all": "Mostra tutti",
        "search-here": "Cerca qui",
        "no-results": "Nessun risultato trovato",
        "no-details": "Nessun dettaglio disponibile",
        "view-details": "Visualizza dettagli",
        "edit-price": "Modifica prezzo",
        "retry-payout": "Riprova pagamento",
        "add-vaccine": "Aggiungi vaccino",
        "add-pet-store": "Aggiungi negozio",
        "add-product": "Aggiungi prodotto",
        "add-specialization": "Aggiungi specializzazione",
        "add-insurance": "Aggiungi assicurazione",
        "add-announcement": "Aggiungi annuncio",
        "edit-product": "Modifica prodotto",
        "edit-pet-store": "Modifica negozio",
        "edit-vaccine": "Modifica vaccino",
        "edit-plan-price": "Modifica prezzo piano",
        "edit-plan": "Modifica piano",
        "edit-specialization": "Modifica specializzazione",
        "edit-insurance-company": "Modifica compagnia assicurativa",
        "edit-announcement": "Modifica annuncio",
        "order-details": "Dettagli ordine",
        "transaction-details": "Dettagli transazione",
        "approve-withdrawal": "Approva prelievo",
        "reject-withdrawal": "Rifiuta prelievo",
        "appointment-details": "Dettagli appuntamento",
        "veterinarian-application-review": "Revisione candidatura veterinario",
        "delivery-commitment-monitoring": "Impegno e monitoraggio consegna",
        "no-active-subscription": "Nessun abbonamento attivo.",
        "purchased-subscription": "Abbonamento acquistato",
        "registration-documents": "Documenti di registrazione",
        "no-registration-documents": "Nessun documento di registrazione caricato.",
        open: "Apri",
        loading: "Caricamento...",
        "no-data": "Nessun dettaglio",
        "save-changes": "Salva modifiche",
        "create-service": "Crea servizio",
        "edit-service": "Modifica servizio",
        "add-service": "Aggiungi servizio",
        "platform-services": "Servizi della piattaforma",
        "footer-options": "Opzioni footer",
        "contact-queries": "Richieste di contatto",
        "support-tickets": "Ticket di assistenza",
        "doctor-messages": "Messaggi ai veterinari",
        "pharmacy-parapharmacy-messages": "Testi farmacia",
        "insurance-companies": "Compagnie assicurative",
        specializations: "Specializzazioni",
        "medical-records": "Cartelle cliniche",
        "withdrawal-requests": "Richieste di prelievo",
        "subscription-plans": "Piani di abbonamento",
        "pet-stores": "Negozi per animali",
        "pet-owner": "Proprietario",
        veterinarian: "Veterinario",
        pharmacy: "Farmacia",
        parapharmacy: "Parafarmacia",
        "pet-sitter": "Pet sitter",
        "name-required": "Il nome è obbligatorio",
        "price-required": "Il prezzo è obbligatorio",
        "owner-id-required": "L'ID del proprietario è obbligatorio",
        "select-at-least-one-species": "Seleziona almeno una specie",
        "delivery-status": "Stato consegna",
        "request-id": "ID richiesta",
        "stripe-connected-account": "Account Stripe collegato",
        "withdrawal-fee-percent-optional": "Percentuale commissione prelievo (facoltativa)",
        fee: "Commissione",
        "net-payout": "Pagamento netto",
        reason: "Motivo",
        number: "N.",
        qty: "Qtà",
        sku: "SKU",
        "discount-price": "Prezzo scontato",
        stock: "Disponibilità",
        "sub-category": "Sottocategoria",
        "pet-types": "Tipi di animali",
        "tags-comma-separated": "Tag (separati da virgola)",
        "requires-prescription": "Richiede prescrizione",
        images: "Immagini",
        logo: "Logo",
        "owner-id": "ID proprietario",
        "address-line-1": "Indirizzo",
        city: "Città",
        state: "Provincia",
        country: "Paese",
        zip: "CAP",
        "applicable-species": "Specie applicabili",
        "min-age-weeks": "Età minima (settimane)",
        "doses-required": "Dosi richieste",
        plan: "Piano",
        title: "Titolo",
        message: "Messaggio",
        link: "Link",
        file: "File",
        priority: "Priorità",
        "announcement-type": "Tipo di annuncio",
        pinned: "In evidenza",
        "expiry-type": "Tipo di scadenza",
        "expiry-date": "Data di scadenza",
        "target-specializations": "Specializzazioni target",
        "target-subscription-plans": "Piani target",
        "target-location-city": "Città target",
        "target-location-state": "Provincia target",
        "target-location-country": "Paese target",
        "individual-veterinarian-ids-comma-separated": "ID veterinari individuali (separati da virgola)",
        active: "Attivo",
        inactive: "Inattivo",
        all: "Tutti",
        "all-priority": "Tutte le priorità",
        "all-ratings": "Tutte le valutazioni",
        "all-species": "Tutte le specie",
        "pinned?": "In evidenza?",
        "not-pinned": "Non in evidenza",
        "pet-store": "Negozio per animali",
        "veterinarian-id": "ID veterinario",
        "order": "Ordine",
        "order-": "Ordine n.",
        "order-requested": "Ordine richiesto",
        "pharmacy-processed": "Farmacia elaborata",
        "shipping-fee-added": "Commissione di spedizione aggiunta",
        "customer-paid": "Cliente pagato",
        "promised-delivery": "Consegna promessa",
        "expected-delivery-date": "Data consegna prevista",
        "actual-delivered": "Consegnato effettivamente",
        "total-actual-delivery-days": "Giorni effettivi totali di consegna",
        "not-set": "Non impostato",
        "late": "in ritardo",
        "days": "giorni",
        day: "giorno",
        years: "anni",
        "license-number": "Numero licenza",
        experience: "Esperienza",
        details: "dettagli",
        showing: "Visualizzazione",
        to: "a",
        of: "di",
        entries: "elementi",
        clear: "Cancella",
        upload: "Carica",
        "pick-upload": "Seleziona e carica",
        "pick-upload-logo": "Seleziona e carica logo",
        "uploading": "Caricamento...",
        view: "Visualizza",
        approve: "Approva",
        reject: "Rifiuta",
        close: "Chiudi",
        save: "Salva",
        create: "Crea",
        edit: "Modifica",
        name: "Nome",
        status: "Stato",
        email: "Email",
        phone: "Telefono",
        type: "Tipo",
        price: "Prezzo",
        description: "Descrizione",
        "video-call-link": "Link videochiamata",
        duration: "Durata",
        symptoms: "Sintomi",
        clinic: "Clinica",
        notes: "Note",
        emergency: "Emergenza",
        amount: "Importo",
        ref: "Riferimento",
        user: "Utente",
        provider: "Fornitore",
        missing: "Mancante",
        items: "Articoli",
        "no-details-available": "Nessun dettaglio disponibile",
        "delete-this-product": "Eliminare questo prodotto?",
        "delete-this-pet-store": "Eliminare questo negozio?",
        "refund-this-transaction": "Rimborsare questa transazione?",
        "delete-this-user-this-cannot-be-undone": "Eliminare questo utente? Questa operazione non può essere annullata.",
        "delete-this-specialization": "Eliminare questa specializzazione?",
        "delete-this-insurance-company": "Eliminare questa compagnia assicurativa?",
        "delete-this-announcement": "Eliminare questo annuncio?",
        "rejection-reason-optional": "Motivo del rifiuto (facoltativo):",
        "delete-this-medical-record-this-cannot-be-undone": "Eliminare questa cartella clinica? Questa operazione non può essere annullata.",
        "yes": "Sì",
        "no": "No",
      },
    },
  },
};

const additionalUiTranslations = {
  en: {
    "platform-services": "Platform Services",
    "this-page-is-ready-for-api-integration": "This page is ready for API integration.",
    "manage-the-public-service-catalogue-and-the-provider-roles-that-may-offer-each-service": "Manage the public service catalogue and the provider roles that may offer each service.",
    service: "Service", "provider-roles": "Provider roles", order: "Order",
    "loading-services": "Loading services...", "no-services-configured": "No services configured.",
    commerce: "Commerce", professional: "Professional", "italian-name": "Italian name", "english-name": "English name",
    "auto-generated-from-english-name": "Auto-generated from English name", "font-awesome-icon": "Font Awesome icon",
    "italian-description": "Italian description", "english-description": "English description",
    "allowed-provider-roles": "Allowed provider roles", "enabled-publicly": "Enabled publicly", saving: "Saving...",
    "unable-to-load-services": "Unable to load services.", "select-at-least-one-provider-role": "Select at least one provider role.",
    "unable-to-save-service": "Unable to save service.", "unable-to-delete-service-you-can-disable-it-instead": "Unable to delete service. You can disable it instead.",
    "unable-to-update-service-status": "Unable to update service status.", "failed-to-load-footer-options": "Failed to load footer options",
    "footer-options-saved-successfully": "Footer options saved successfully.", "failed-to-save-footer-options": "Failed to save footer options",
    "these-values-appear-in-the-public-website-footer-and-contact-us-page": "These values appear in the public website footer and Contact Us page.",
    "loading-footer-options": "Loading footer options...", "address-location": "Address / Location", "support-email": "Support Email",
    "phone-number": "Phone Number", "social-media-links": "Social Media Links", "only-active-links-are-shown-in-the-website-footer": "Only active links are shown in the website footer.",
    "add-link": "Add Link", "no-social-links-configured": "No social links configured.", platform: "Platform", remove: "Remove", "save-footer-options": "Save Footer Options",
    "failed-to-load-contact-us-queries": "Failed to load Contact Us queries", "failed-to-update-contact-us-query": "Failed to update Contact Us query",
    "write-a-response-email-before-resolving-this-query": "Write a response email before resolving this query.",
    "failed-to-send-the-response-email-and-resolve-this-query": "Failed to send the response email and resolve this query",
    "delete-this-contact-us-query": "Delete this Contact Us query?", "failed-to-delete-contact-us-query": "Failed to delete Contact Us query",
    "review-and-manage-messages-submitted-from-the-public-website": "Review and manage messages submitted from the public website.", total: "total",
    "search-name-email-phone-service-or-message": "Search name, email, phone, service or message", "all-statuses": "All statuses",
    submitted: "Submitted", contact: "Contact", manage: "Manage", page: "Page", of: "of", previous: "Previous", next: "Next", message: "Message", "no-contact-us-queries-found": "No Contact Us queries found.",
    "manage-contact-us-query": "Manage Contact Us Query", "send-response-resolve": "Send Response & Resolve", "response-email": "Response Email",
    "write-the-response-that-will-be-emailed-to-the-customer": "Write the response that will be emailed to the customer...", "response-sent-on": "Response sent on", "private-admin-notes": "Private Admin Notes",
    "unable-to-load-admin-messages": "Unable to load admin messages.", "the-conversation-could-not-be-created": "The conversation could not be created.", "unable-to-start-the-conversation": "Unable to start the conversation.",
    "choose-or-start-a-conversation-first": "Choose or start a conversation first.", "unable-to-send-the-message": "Unable to send the message.",
    "choose-or-start-a-conversation-before-attaching-files": "Choose or start a conversation before attaching files.", "each-attachment-must-be-50-mb-or-smaller": "Each attachment must be 50 MB or smaller.",
    "unable-to-upload-the-selected-files": "Unable to upload the selected files.", "unable-to-upload-file": "Unable to upload file.",
    "choose-pharmacy-or-parapharmacy-conversations": "Choose Pharmacy or Parapharmacy conversations", "secure-conversations-and-file-sharing-with-pharmacies-and-parapharmacies": "Secure conversations and file sharing with pharmacies and parapharmacies.",
    "secure-conversations-and-file-sharing-with-veterinarians": "Secure conversations and file sharing with veterinarians.", "start-a-conversation-with-a": "Start a conversation with a", conversations: "Conversations",
    "loading-conversations": "Loading conversations...", "no-messages-yet": "No messages yet", "no-messages-yet-say-hello": "No messages yet. Say hello.", "no-messages-yet-start-one-using-the-selector-above": "No messages yet. Start one using the selector above.",
    "messages-yet-start-one-using-the-selector-above": "messages yet. Start one using the selector above.", "loading-messages": "Loading messages...", "open-attachment": "Open attachment", attachment: "Attachment", "attach-files": "Attach files",
    "write-a-secure-message": "Write a secure message...", uploading: "Uploading...", sending: "Sending...", send: "Send", "select-a-conversation-or-start-a-new-conversation": "Select a conversation or start a new conversation.",
    "unable-to-load-support-tickets": "Unable to load support tickets.", "review-pet-owner-issues-keep-responses-inside-each-ticket-and-track-the-full-support-history": "Review pet-owner issues, keep responses inside each ticket, and track the full support history.",
    refresh: "Refresh", "search-ticket-number-or-subject": "Search ticket number or subject", "all-categories": "All categories", "all-priorities": "All priorities", "no-matching-support-tickets": "No matching support tickets",
    "new-pet-owner-requests-will-automatically-appear-here": "New pet-owner requests will automatically appear here.", ticket: "Ticket", "my-pet": "My Pet", "last-updated": "Last updated", new: "New",
    "unable-to-load-this-support-ticket": "Unable to load this support ticket.", "unable-to-update-this-ticket": "Unable to update this ticket.", "you-can-attach-up-to-5-files": "You can attach up to 5 files.", "each-attachment-must-be-25-mb-or-smaller": "Each attachment must be 25 MB or smaller.",
    "unable-to-send-the-reply": "Unable to send the reply.", "unable-to-download-the-attachment": "Unable to download the attachment.", "back-to-support-tickets": "Back to support tickets", "ticket-conversation": "Ticket conversation",
    "change-this-ticket-back-to-open-or-in-progress-before-replying": "Change this ticket back to Open or In Progress before replying.", "reply-to-patient": "Reply to patient", "write-a-clear-update-or-ask-for-the-information-you-need": "Write a clear update or ask for the information you need...", "send-reply": "Send reply",
    "manage-ticket": "Manage ticket", "patient-request": "Patient & request", patient: "Patient", record: "Record", "view-record": "View record", "activity-timeline": "Activity timeline", admin: "Admin",
    "waiting-for-patient": "Waiting For Patient", "in-progress": "In Progress", resolved: "Resolved", closed: "Closed", open: "Open", low: "Low", medium: "Medium", high: "High", urgent: "Urgent",
    appointment: "Appointment", reschedule: "Reschedule", "video-call": "Video Call", payment: "Payment", "pharmacy-order": "Pharmacy Order", "parapharmacy-order": "Parapharmacy Order", delivery: "Delivery", refund: "Refund", prescription: "Prescription", "account-registration": "Account Registration", "pet-profile": "Pet Profile", veterinarian: "Veterinarian", technical: "Technical", other: "Other",
    "start-a-conversation-with-a-veterinarian": "Start a conversation with a veterinarian", "start-a-conversation-with-a-pharmacy": "Start a conversation with a pharmacy", "start-a-conversation-with-a-parapharmacy": "Start a conversation with a parapharmacy",
    "verify-your-registered-email-before-creating-a-new-admin-password": "Verify your registered email before creating a new Admin password.", "request-code": "Request code", "verify-email": "Verify email", "new-password": "New password", "a-verification-code-will-be-sent-to": "A verification code will be sent to", "only-when-you-click-the-button-below": "only when you click the button below.", "send-verification-code": "Send Verification Code", "verification-code": "Verification code", "enter-the-6-digit-code": "Enter the 6-digit code", "verify-code": "Verify Code", "resend-code": "Resend Code", "confirm-new-password": "Confirm new password", "change-password": "Change Password", "a-verification-code-has-been-sent-to": "A verification code has been sent to", "unable-to-send-the-verification-code": "Unable to send the verification code.", "enter-the-6-digit-verification-code-from-your-email": "Enter the 6-digit verification code from your email.", "code-verified-you-can-now-set-your-new-password": "Code verified. You can now set your new password.", "the-verification-code-is-invalid-or-expired": "The verification code is invalid or expired.", "the-new-password-must-be-at-least-8-characters-long": "The new password must be at least 8 characters long.", "the-new-password-and-confirmation-do-not-match": "The new password and confirmation do not match.", "your-password-has-been-changed-successfully": "Your password has been changed successfully.", "unable-to-change-the-password": "Unable to change the password.",
    pending: "Pending", approved: "Approved", blocked: "Blocked", rejected: "Rejected", active: "Active", inactive: "Inactive", activate: "Activate", deactivate: "Deactivate", preview: "Preview", completed: "Completed", cancelled: "Cancelled", expired: "Expired", confirmed: "Confirmed", processing: "Processing", shipped: "Shipped", delivered: "Delivered", refunded: "Refunded", success: "Success", failed: "Failed", none: "None", unpaid: "Unpaid", partial: "Partial", paid: "Paid", rescheduled: "Rescheduled", "no-show": "No Show", "pending-payment": "Pending Payment", yes: "Yes", no: "No", "pet-owner": "Pet Owner", "pet-sitter": "Pet Sitter", "pet-store": "Pharmacy", parapharmacy: "Parapharmacy", pharmacy: "Pharmacy", dog: "Dog", cat: "Cat", bird: "Bird", rabbit: "Rabbit", "lab-report": "Lab Report", xray: "X-ray", vaccination: "Vaccination", subscription: "Subscription", product: "Product", normal: "Normal", important: "Important", broadcast: "Broadcast", targeted: "Targeted", "no-expiry": "No expiry", "expire-after-date": "Expire after date", "auto-hide-after-read": "Auto-hide after read", url: "URL", slug: "Slug", "all-types": "All Types", "all-subscriptions": "All Subscriptions", "all-status": "All Status", "open-link": "Open link",
    "failed-to-save-product": "Failed to save product", "failed-to-delete-product": "Failed to delete product", "failed-to-save-pet-store": "Failed to save pet store", "failed-to-delete-pet-store": "Failed to delete pet store", "failed-to-load-order-details": "Failed to load order details", "failed-to-update-order-status": "Failed to update order status", "failed-to-load-transaction-details": "Failed to load transaction details", "failed-to-update-transaction-status": "Failed to update transaction status", "failed-to-refund-transaction": "Failed to refund transaction", "failed-to-delete-user": "Failed to delete user", "failed-to-approve-withdrawal": "Failed to approve withdrawal", "failed-to-reject-withdrawal": "Failed to reject withdrawal", "failed-to-upload-file": "Failed to upload file", "failed-to-upload-files": "Failed to upload files", "failed-to-load-data": "Failed to load data", "failed-to-save-vaccine": "Failed to save vaccine", "failed-to-load-appointment-details": "Failed to load appointment details", "failed-to-update-plan": "Failed to update plan", "failed-to-save-specialization": "Failed to save specialization", "failed-to-delete-specialization": "Failed to delete specialization", "failed-to-save-insurance-company": "Failed to save insurance company", "failed-to-update-status": "Failed to update status", "failed-to-delete-insurance-company": "Failed to delete insurance company", "failed-to-save-announcement": "Failed to save announcement", "failed-to-delete-announcement": "Failed to delete announcement", "failed-to-approve-veterinarian": "Failed to approve veterinarian", "failed-to-reject-veterinarian": "Failed to reject veterinarian", "failed-to-approve-account": "Failed to approve account", "failed-to-reject-account": "Failed to reject account", "failed-to-delete-medical-record": "Failed to delete medical record", "failed-to-update-user-status": "Failed to update user status",
  },
  it: {
    "platform-services": "Servizi della piattaforma",
    "this-page-is-ready-for-api-integration": "Questa pagina è pronta per l'integrazione API.",
    "manage-the-public-service-catalogue-and-the-provider-roles-that-may-offer-each-service": "Gestisci il catalogo pubblico dei servizi e i ruoli dei fornitori che possono offrire ogni servizio.",
    service: "Servizio", "provider-roles": "Ruoli dei fornitori", order: "Ordine", "loading-services": "Caricamento servizi...", "no-services-configured": "Nessun servizio configurato.",
    commerce: "Commercio", professional: "Professionale", "italian-name": "Nome italiano", "english-name": "Nome inglese", "auto-generated-from-english-name": "Generato automaticamente dal nome inglese", "font-awesome-icon": "Icona Font Awesome",
    "italian-description": "Descrizione italiana", "english-description": "Descrizione inglese", "allowed-provider-roles": "Ruoli fornitori consentiti", "enabled-publicly": "Abilitato pubblicamente", saving: "Salvataggio...",
    "unable-to-load-services": "Impossibile caricare i servizi.", "select-at-least-one-provider-role": "Seleziona almeno un ruolo fornitore.", "unable-to-save-service": "Impossibile salvare il servizio.", "unable-to-delete-service-you-can-disable-it-instead": "Impossibile eliminare il servizio. Puoi disabilitarlo.", "unable-to-update-service-status": "Impossibile aggiornare lo stato del servizio.",
    "failed-to-load-footer-options": "Impossibile caricare le opzioni del footer", "footer-options-saved-successfully": "Opzioni del footer salvate correttamente.", "failed-to-save-footer-options": "Impossibile salvare le opzioni del footer", "these-values-appear-in-the-public-website-footer-and-contact-us-page": "Questi valori appaiono nel footer del sito pubblico e nella pagina Contatti.",
    "loading-footer-options": "Caricamento opzioni footer...", "address-location": "Indirizzo / Posizione", "support-email": "Email di assistenza", "phone-number": "Numero di telefono", "social-media-links": "Link social", "only-active-links-are-shown-in-the-website-footer": "Solo i link attivi vengono mostrati nel footer del sito.", "add-link": "Aggiungi link", "no-social-links-configured": "Nessun link social configurato.", platform: "Piattaforma", remove: "Rimuovi", "save-footer-options": "Salva opzioni footer",
    "failed-to-load-contact-us-queries": "Impossibile caricare le richieste Contatti", "failed-to-update-contact-us-query": "Impossibile aggiornare la richiesta Contatti", "write-a-response-email-before-resolving-this-query": "Scrivi un'email di risposta prima di risolvere questa richiesta.", "failed-to-send-the-response-email-and-resolve-this-query": "Impossibile inviare l'email di risposta e risolvere questa richiesta", "delete-this-contact-us-query": "Eliminare questa richiesta Contatti?", "failed-to-delete-contact-us-query": "Impossibile eliminare la richiesta Contatti",
    "review-and-manage-messages-submitted-from-the-public-website": "Esamina e gestisci i messaggi inviati dal sito pubblico.", total: "totale", "search-name-email-phone-service-or-message": "Cerca nome, email, telefono, servizio o messaggio", "all-statuses": "Tutti gli stati", submitted: "Inviato", contact: "Contatto", manage: "Gestisci", page: "Pagina", of: "di", previous: "Precedente", next: "Successiva", message: "Messaggio", "no-contact-us-queries-found": "Nessuna richiesta Contatti trovata.", "manage-contact-us-query": "Gestisci richiesta Contatti", "send-response-resolve": "Invia risposta e risolvi", "response-email": "Email di risposta", "write-the-response-that-will-be-emailed-to-the-customer": "Scrivi la risposta che verra inviata via email al cliente...", "response-sent-on": "Risposta inviata il", "private-admin-notes": "Note private dell'amministratore",
    "unable-to-load-admin-messages": "Impossibile caricare i messaggi amministrativi.", "the-conversation-could-not-be-created": "Non e stato possibile creare la conversazione.", "unable-to-start-the-conversation": "Impossibile avviare la conversazione.", "choose-or-start-a-conversation-first": "Scegli o avvia prima una conversazione.", "unable-to-send-the-message": "Impossibile inviare il messaggio.", "choose-or-start-a-conversation-before-attaching-files": "Scegli o avvia una conversazione prima di allegare file.", "each-attachment-must-be-50-mb-or-smaller": "Ogni allegato deve avere dimensione massima di 50 MB.", "unable-to-upload-the-selected-files": "Impossibile caricare i file selezionati.", "unable-to-upload-file": "Impossibile caricare il file.",
    "choose-pharmacy-or-parapharmacy-conversations": "Scegli le conversazioni di farmacie o parafarmacie", "secure-conversations-and-file-sharing-with-pharmacies-and-parapharmacies": "Conversazioni sicure e condivisione di file con farmacie e parafarmacie.", "secure-conversations-and-file-sharing-with-veterinarians": "Conversazioni sicure e condivisione di file con veterinari.", "start-a-conversation-with-a": "Avvia una conversazione con un", conversations: "Conversazioni", "loading-conversations": "Caricamento conversazioni...", "no-messages-yet": "Nessun messaggio", "no-messages-yet-say-hello": "Nessun messaggio. Saluta per iniziare.", "no-messages-yet-start-one-using-the-selector-above": "Nessun messaggio. Avviane una usando il selettore qui sopra.", "messages-yet-start-one-using-the-selector-above": "messaggi. Avviane una usando il selettore qui sopra.", "loading-messages": "Caricamento messaggi...", "open-attachment": "Apri allegato", attachment: "Allegato", "attach-files": "Allega file", "write-a-secure-message": "Scrivi un messaggio sicuro...", uploading: "Caricamento...", sending: "Invio...", send: "Invia", "select-a-conversation-or-start-a-new-conversation": "Seleziona una conversazione o avviane una nuova.",
    "unable-to-load-support-tickets": "Impossibile caricare i ticket di assistenza.", "review-pet-owner-issues-keep-responses-inside-each-ticket-and-track-the-full-support-history": "Esamina i problemi dei proprietari, mantieni le risposte nel ticket e segui tutta la cronologia dell'assistenza.", refresh: "Aggiorna", "search-ticket-number-or-subject": "Cerca numero ticket o oggetto", "all-categories": "Tutte le categorie", "all-priorities": "Tutte le priorita", "no-matching-support-tickets": "Nessun ticket corrispondente", "new-pet-owner-requests-will-automatically-appear-here": "Le nuove richieste dei proprietari appariranno automaticamente qui.", ticket: "Ticket", "my-pet": "Il mio animale", "last-updated": "Ultimo aggiornamento", new: "Nuovo",
    "unable-to-load-this-support-ticket": "Impossibile caricare questo ticket di assistenza.", "unable-to-update-this-ticket": "Impossibile aggiornare questo ticket.", "you-can-attach-up-to-5-files": "Puoi allegare fino a 5 file.", "each-attachment-must-be-25-mb-or-smaller": "Ogni allegato deve avere dimensione massima di 25 MB.", "unable-to-send-the-reply": "Impossibile inviare la risposta.", "unable-to-download-the-attachment": "Impossibile scaricare l'allegato.", "back-to-support-tickets": "Torna ai ticket di assistenza", "ticket-conversation": "Conversazione del ticket", "change-this-ticket-back-to-open-or-in-progress-before-replying": "Riporta il ticket a Aperto o In corso prima di rispondere.", "reply-to-patient": "Rispondi al proprietario", "write-a-clear-update-or-ask-for-the-information-you-need": "Scrivi un aggiornamento chiaro o chiedi le informazioni necessarie...", "send-reply": "Invia risposta", "manage-ticket": "Gestisci ticket", "patient-request": "Proprietario e richiesta", patient: "Proprietario", record: "Record", "view-record": "Visualizza record", "activity-timeline": "Cronologia attivita", admin: "Amministratore",
    "waiting-for-patient": "In attesa del proprietario", "in-progress": "In corso", resolved: "Risolto", closed: "Chiuso", open: "Aperto", low: "Bassa", medium: "Media", high: "Alta", urgent: "Urgente", appointment: "Appuntamento", reschedule: "Riprogrammazione", "video-call": "Videochiamata", payment: "Pagamento", "pharmacy-order": "Ordine farmacia", "parapharmacy-order": "Ordine parafarmacia", delivery: "Consegna", refund: "Rimborso", prescription: "Prescrizione", "account-registration": "Registrazione account", "pet-profile": "Profilo animale", veterinarian: "Veterinario", technical: "Tecnico", other: "Altro",
    "start-a-conversation-with-a-veterinarian": "Avvia una conversazione con un veterinario", "start-a-conversation-with-a-pharmacy": "Avvia una conversazione con una farmacia", "start-a-conversation-with-a-parapharmacy": "Avvia una conversazione con una parafarmacia",
    "verify-your-registered-email-before-creating-a-new-admin-password": "Verifica l'email registrata prima di creare una nuova password amministratore.", "request-code": "Richiedi codice", "verify-email": "Verifica email", "new-password": "Nuova password", "a-verification-code-will-be-sent-to": "Un codice di verifica verra inviato a", "only-when-you-click-the-button-below": "solo quando farai clic sul pulsante qui sotto.", "send-verification-code": "Invia codice di verifica", "verification-code": "Codice di verifica", "enter-the-6-digit-code": "Inserisci il codice di 6 cifre", "verify-code": "Verifica codice", "resend-code": "Invia di nuovo il codice", "confirm-new-password": "Conferma nuova password", "change-password": "Cambia password", "a-verification-code-has-been-sent-to": "Un codice di verifica e stato inviato a", "unable-to-send-the-verification-code": "Impossibile inviare il codice di verifica.", "enter-the-6-digit-verification-code-from-your-email": "Inserisci il codice di verifica di 6 cifre ricevuto via email.", "code-verified-you-can-now-set-your-new-password": "Codice verificato. Ora puoi impostare la nuova password.", "the-verification-code-is-invalid-or-expired": "Il codice di verifica non e valido o e scaduto.", "the-new-password-must-be-at-least-8-characters-long": "La nuova password deve contenere almeno 8 caratteri.", "the-new-password-and-confirmation-do-not-match": "La nuova password e la conferma non coincidono.", "your-password-has-been-changed-successfully": "La password e stata modificata correttamente.", "unable-to-change-the-password": "Impossibile modificare la password.",
    pending: "In attesa", approved: "Approvato", blocked: "Bloccato", rejected: "Rifiutato", active: "Attivo", inactive: "Inattivo", activate: "Attiva", deactivate: "Disattiva", preview: "Anteprima", completed: "Completato", cancelled: "Annullato", expired: "Scaduto", confirmed: "Confermato", processing: "In elaborazione", shipped: "Spedito", delivered: "Consegnato", refunded: "Rimborsato", success: "Riuscito", failed: "Fallito", none: "Nessuno", unpaid: "Non pagato", partial: "Parziale", paid: "Pagato", rescheduled: "Riprogrammato", "no-show": "Assente", "pending-payment": "Pagamento in attesa", yes: "Si", no: "No", "pet-owner": "Proprietario", "pet-sitter": "Pet sitter", "pet-store": "Farmacia", parapharmacy: "Parafarmacia", pharmacy: "Farmacia", dog: "Cane", cat: "Gatto", bird: "Uccello", rabbit: "Coniglio", "lab-report": "Referto di laboratorio", xray: "Radiografia", vaccination: "Vaccinazione", subscription: "Abbonamento", product: "Prodotto", normal: "Normale", important: "Importante", broadcast: "Diffusione", targeted: "Mirato", "no-expiry": "Nessuna scadenza", "expire-after-date": "Scade dopo la data", "auto-hide-after-read": "Nascondi dopo la lettura", url: "URL", slug: "Slug", "all-types": "Tutti i tipi", "all-subscriptions": "Tutti gli abbonamenti", "all-status": "Tutti gli stati", "open-link": "Apri link",
    "failed-to-save-product": "Impossibile salvare il prodotto", "failed-to-delete-product": "Impossibile eliminare il prodotto", "failed-to-save-pet-store": "Impossibile salvare il negozio", "failed-to-delete-pet-store": "Impossibile eliminare il negozio", "failed-to-load-order-details": "Impossibile caricare i dettagli dell'ordine", "failed-to-update-order-status": "Impossibile aggiornare lo stato dell'ordine", "failed-to-load-transaction-details": "Impossibile caricare i dettagli della transazione", "failed-to-update-transaction-status": "Impossibile aggiornare lo stato della transazione", "failed-to-refund-transaction": "Impossibile rimborsare la transazione", "failed-to-delete-user": "Impossibile eliminare l'utente", "failed-to-approve-withdrawal": "Impossibile approvare il prelievo", "failed-to-reject-withdrawal": "Impossibile rifiutare il prelievo", "failed-to-upload-file": "Impossibile caricare il file", "failed-to-upload-files": "Impossibile caricare i file", "failed-to-load-data": "Impossibile caricare i dati", "failed-to-save-vaccine": "Impossibile salvare il vaccino", "failed-to-load-appointment-details": "Impossibile caricare i dettagli dell'appuntamento", "failed-to-update-plan": "Impossibile aggiornare il piano", "failed-to-save-specialization": "Impossibile salvare la specializzazione", "failed-to-delete-specialization": "Impossibile eliminare la specializzazione", "failed-to-save-insurance-company": "Impossibile salvare la compagnia assicurativa", "failed-to-update-status": "Impossibile aggiornare lo stato", "failed-to-delete-insurance-company": "Impossibile eliminare la compagnia assicurativa", "failed-to-save-announcement": "Impossibile salvare l'annuncio", "failed-to-delete-announcement": "Impossibile eliminare l'annuncio", "failed-to-approve-veterinarian": "Impossibile approvare il veterinario", "failed-to-reject-veterinarian": "Impossibile rifiutare il veterinario", "failed-to-approve-account": "Impossibile approvare l'account", "failed-to-reject-account": "Impossibile rifiutare l'account", "failed-to-delete-medical-record": "Impossibile eliminare la cartella clinica", "failed-to-update-user-status": "Impossibile aggiornare lo stato dell'utente",
  },
};

const getInitialLanguage = () => {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return saved === "en" || saved === "it" ? saved : DEFAULT_LANGUAGE;
};

const getByPath = (source, path) => String(path).split(".").reduce((value, part) => value?.[part], source);

const clearGoogleTranslateCookies = () => {
  if (typeof document === "undefined") return;
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `googtrans=;path=/;${expired}`;
  if (typeof window !== "undefined" && window.location.hostname) {
    document.cookie = `googtrans=;path=/;domain=${window.location.hostname};${expired}`;
  }
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    setLanguageState(nextLanguage === "en" ? "en" : "it");
  }, []);

  useEffect(() => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
    clearGoogleTranslateCookies();
  }, [language]);

  const t = useCallback((key, variablesOrFallback = {}, maybeVariables = {}) => {
    const variables = typeof variablesOrFallback === "object" ? variablesOrFallback : maybeVariables;
    const fallback = typeof variablesOrFallback === "string" ? variablesOrFallback : key;
    const translated = getByPath(translations[language], key) ?? getByPath(translations.en, key) ?? fallback;
    return String(translated).replace(/\{(\w+)\}/g, (_, name) => variables[name] ?? `{${name}}`);
  }, [language]);

  const translateText = useCallback((value) => {
    const original = String(value ?? "");
    const normalized = original.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return normalized
      ? additionalUiTranslations[language]?.[normalized] ?? getByPath(translations[language], `admin.labels.${normalized}`) ?? additionalUiTranslations.en?.[normalized] ?? getByPath(translations.en, `admin.labels.${normalized}`) ?? original
      : original;
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    toggleLanguage: () => setLanguageState((current) => current === "it" ? "en" : "it"),
    t,
    translateText,
    translations,
  }), [language, setLanguage, t, translateText]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within a LanguageProvider");
  return context;
};

export default LanguageContext;
